/* ─── köbi Hero Slider ─────────────────────────────────────────────── */

const KOEBI_HERO_LABEL_UPLOAD = {
  dbName: 'koebi-label-upload',
  storeName: 'uploads',
  key: 'pending-hero-upload',
  flag: 'koebiLabelPendingUpload',
  maxBytes: 20 * 1024 * 1024,
};

function openKoebiLabelUploadDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB nicht verfuegbar'));
      return;
    }

    const request = window.indexedDB.open(KOEBI_HERO_LABEL_UPLOAD.dbName, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(KOEBI_HERO_LABEL_UPLOAD.storeName)) {
        db.createObjectStore(KOEBI_HERO_LABEL_UPLOAD.storeName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB konnte nicht geoeffnet werden'));
  });
}

async function saveKoebiPendingLabelUpload(file) {
  const db = await openKoebiLabelUploadDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(KOEBI_HERO_LABEL_UPLOAD.storeName, 'readwrite');
      tx.objectStore(KOEBI_HERO_LABEL_UPLOAD.storeName).put({
        blob: file,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        createdAt: Date.now(),
      }, KOEBI_HERO_LABEL_UPLOAD.key);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error || new Error('Upload konnte nicht gespeichert werden'));
      tx.onabort = () => reject(tx.error || new Error('Upload-Speicherung abgebrochen'));
    });
  } finally {
    db.close();
  }
  window.sessionStorage.setItem(KOEBI_HERO_LABEL_UPLOAD.flag, '1');
}

function validateKoebiLabelUploadFile(file) {
  if (!file) return 'Bitte waehle eine Datei aus.';
  if (file.size > KOEBI_HERO_LABEL_UPLOAD.maxBytes) {
    return 'Datei groesser als 20 MB.';
  }

  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  const isPdf = mime === 'application/pdf' || name.endsWith('.pdf');
  const isImage = mime.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(name);
  if (!isPdf && !isImage) {
    return 'Bitte PDF, PNG, JPG oder WebP hochladen.';
  }
  return '';
}

function buildKoebiLabelTargetUrl(rawUrl, anchor) {
  try {
    const url = new URL(rawUrl || '/products/dein-etikett-auf-unserem-bier', window.location.origin);
    url.hash = anchor || 'koebi-label-editor';
    return url.href;
  } catch (error) {
    return `/products/dein-etikett-auf-unserem-bier#${anchor || 'koebi-label-editor'}`;
  }
}

class KoebiHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.track       = this.querySelector('.koebi-hero__track');
    this.wrapper     = this.querySelector('.koebi-hero__track-wrapper');
    this.slides      = this.querySelectorAll('.koebi-hero__slide');
    this.dots        = this.querySelectorAll('.koebi-hero__dot');
    this.prevBtn     = this.querySelector('.koebi-hero__arrow--prev');
    this.nextBtn     = this.querySelector('.koebi-hero__arrow--next');
    this.totalSlides = this.slides.length;
  }

  connectedCallback() {
    this.kickstartVideos();
    this.bindVideoControls();
    this.bindLabelUploads();
    this.bindEditorEvents();

    if (!this.track || !this.wrapper || this.totalSlides === 0) return;

    // Auf DOM-Paint warten, damit offsetHeight korrekte Werte liefert
    requestAnimationFrame(() => {
      if (this.totalSlides > 1) {
        this.bindEvents();
        this.update();
      } else {
        this.updateHeight(true);
      }
      this.bindResizeObserver();
    });
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Touch / Swipe
    let startX = 0;
    this.track.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 48) {
        diff > 0 ? this.next() : this.prev();
      }
    }, { passive: true });

    // Keyboard
    this.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  goTo(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.totalSlides - 1));
    this.update();
  }

  next() { this.goTo(this.currentIndex + 1); }
  prev() { this.goTo(this.currentIndex - 1); }

  kickstartVideos(scope = this) {
    scope.querySelectorAll('.koebi-hero__video').forEach(video => {
      try {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.loop = true;
        video.setAttribute('loop', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');

        if (video.dataset.koebiHeroVideoBound !== 'true') {
          video.dataset.koebiHeroVideoBound = 'true';
          video.addEventListener('ended', () => {
            if (video.dataset.koebiHeroUserPaused === 'true') return;
            video.currentTime = 0;
            if (typeof window.koebiPrepareAutoplayVideo === 'function') {
              window.koebiPrepareAutoplayVideo(video);
            } else {
              const replay = video.play();
              if (replay && typeof replay.catch === 'function') replay.catch(() => {});
            }
          });
        }

        if (typeof window.koebiPrepareAutoplayVideo === 'function') {
          window.koebiPrepareAutoplayVideo(video);
        } else {
          if (video.dataset.koebiHeroUserPaused !== 'true') {
            const play = video.play();
            if (play && typeof play.catch === 'function') play.catch(() => {});
          }
        }
      } catch (error) {}
    });
  }

  // Video Play/Pause Toggle
  bindVideoControls() {
    this.querySelectorAll('.koebi-hero__video-control').forEach(btn => {
      if (btn.dataset.koebiHeroControlBound === 'true') return;

      const video = btn.closest('.koebi-hero__image-panel, .koebi-hero__card-image')?.querySelector('.koebi-hero__video');
      if (!video) return;

      btn.dataset.koebiHeroControlBound = 'true';
      btn.addEventListener('click', () => {
        const paused = video.paused;
        if (paused) {
          delete video.dataset.koebiHeroUserPaused;
          const play = video.play();
          if (play && typeof play.catch === 'function') play.catch(() => {});
        } else {
          video.dataset.koebiHeroUserPaused = 'true';
          video.pause();
        }
        btn.setAttribute('aria-pressed', paused ? 'false' : 'true');
        btn.setAttribute('aria-label', paused ? 'Video pausieren' : 'Video abspielen');
      });
    });
  }

  bindLabelUploads() {
    this.querySelectorAll('[data-koebi-hero-upload-zone]').forEach(zone => {
      if (zone.dataset.koebiHeroUploadBound === 'true') return;

      const input = zone.parentElement?.querySelector('[data-koebi-hero-upload]');
      const status = zone.querySelector('[data-koebi-hero-upload-status]');
      if (!input) return;

      zone.dataset.koebiHeroUploadBound = 'true';

      const setStatus = (message, tone = '') => {
        if (status) status.textContent = message;
        zone.dataset.uploadTone = tone;
      };

      const handleFile = async (file) => {
        const error = validateKoebiLabelUploadFile(file);
        if (error) {
          setStatus(error, 'error');
          return;
        }

        try {
          zone.classList.add('is-uploading');
          setStatus('Upload wird vorbereitet...');
          await saveKoebiPendingLabelUpload(file);
          setStatus('Weiter zum Designer...');
          window.location.href = buildKoebiLabelTargetUrl(zone.dataset.targetUrl, zone.dataset.targetAnchor);
        } catch (err) {
          console.error('[koebi-hero] Label-Upload konnte nicht vorbereitet werden:', err);
          zone.classList.remove('is-uploading');
          setStatus('Bitte direkt im Designer hochladen.', 'error');
        }
      };

      zone.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          input.click();
        }
      });

      ['dragenter', 'dragover'].forEach(eventName => {
        zone.addEventListener(eventName, event => {
          event.preventDefault();
          zone.classList.add('is-dragover');
        });
      });

      ['dragleave', 'dragend'].forEach(eventName => {
        zone.addEventListener(eventName, event => {
          event.preventDefault();
          zone.classList.remove('is-dragover');
        });
      });

      zone.addEventListener('drop', event => {
        event.preventDefault();
        zone.classList.remove('is-dragover');
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        handleFile(file);
      });

      input.addEventListener('change', () => {
        const file = input.files && input.files[0];
        handleFile(file);
      });
    });
  }

  // Theme Editor: Slide automatisch anzeigen wenn Block in Sidebar angeklickt
  bindEditorEvents() {
    if (!window.Shopify || !Shopify.designMode) return;

    this.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest('.koebi-hero__slide');
      if (!slide) return;
      const index = Array.from(this.slides).indexOf(slide);
      if (index !== -1) this.goTo(index);
    });
  }

  update() {
    // Slide-Position
    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;

    // Höhe animiert anpassen (synchron mit dem Slide)
    this.updateHeight();

    // Dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('koebi-hero__dot--active', i === this.currentIndex);
      dot.classList.toggle('slider-counter__link--active', i === this.currentIndex);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
    });

    // Pfeile
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex === this.totalSlides - 1;
    }

    const activeSlide = this.slides[this.currentIndex];
    if (activeSlide) this.kickstartVideos(activeSlide);
  }

  updateHeight(instant = false) {
    // Nur auf Mobile aktiv
    if (window.innerWidth > 989) {
      this.wrapper.style.height = '';
      return;
    }

    const slide = this.slides[this.currentIndex];
    const h = slide.offsetHeight || slide.scrollHeight;
    if (!h) return;

    if (instant) {
      // Ohne Transition setzen, dann im nächsten Frame wieder aktivieren
      this.wrapper.style.transition = 'none';
      this.wrapper.style.height = h + 'px';
      requestAnimationFrame(() => {
        this.wrapper.style.transition = '';
      });
    } else {
      this.wrapper.style.height = h + 'px';
    }
  }

  bindResizeObserver() {
    // Bei Viewport-Resize Höhe neu berechnen (z.B. Rotation, Zoom)
    const ro = new ResizeObserver(() => this.updateHeight(true));
    this.slides.forEach(s => ro.observe(s));
  }
}

customElements.define('koebi-hero-slider', KoebiHeroSlider);
